import { rem } from 'polished'
import React, {
  useCallback,
  useEffect,
  useState,
  ComponentProps,
  ChangeEventHandler,
  FocusEventHandler,
  useMemo,
  useRef,
} from 'react'
import { useFormContext } from 'react-hook-form'
import styled from 'styled-components'

import BaseAutocompleteInput from './BaseAutocompleteInput'
import SearchBookmarkButtonFromPlaceId from '../Bookmark/BookmarkButton/Search/SearchBookmarkFromPlaceId'
import MapSearchPresentationSelector from '../Search/MapSearchPresentationSelector'
import NoResultsCopy from '../Search/NoResultsCopy'
import { ACCOMMODATION_TYPE, determineItemType } from '../TripMaps/MapSearch'
import Input from '../TripModal/TripItems/Common/Fields/RHFInput'

import * as Analytics from 'analytics/analytics'
import Clickable from 'components/Common/Clickable'
import LineBedIcon from 'components/Luxkit/Icons/line/LineBedIcon'
import LineTimesIcon from 'components/Luxkit/Icons/line/LineTimesIcon'
import LineUniversityIcon from 'components/Luxkit/Icons/line/LineUniversityIcon'
import { AnalyticsPage } from 'contexts/Analytics/analyticsPageContext'
import useAutoUpdatingRef from 'hooks/useAutoUpdatingRef'
import { GooglePlaceAutocompleteRequest } from 'tripPlanner/api/places'
import { TripGooglePlaceAutocompletePrediction } from 'tripPlanner/api/places/types'
import { useTripExplorePlaceDetails } from 'tripPlanner/hooks/Google/useTripExplorePlaceDetail'
import useTripPlaceAutocomplete from 'tripPlanner/hooks/Google/useTripPlaceAutocomplete'
import { ExploreItem } from 'tripPlanner/types/common'
import {
  AutocompletePlaceParam,
  AutocompletionRequest,
} from 'tripPlanner/types/google'
import { AccommodationItem } from 'tripPlanner/types/tripItem'

const StyledClickable = styled(Clickable)`
  margin-top: ${rem(3)};
`

const DEBOUNCE_DELAY = 300
export const CHARACTER_THRESHOLD = 1
const NO_RESULTS_MESSAGE = 'Try a different search phrase'

interface Props extends ComponentProps<typeof Input> {
  autocompleteTypes: AutocompletePlaceParam
  label?: string
  name: string
  locationBias?: { lat: number; lng: number }
  onPlaceSelect: (item: ExploreItem) => void
  accommodationItems: Array<AccommodationItem>
  /** substitute for onBlur in fullScreen mode */
  onFullScreenBlur?: () => void
  placeholder?: string
  onEnterPress?: (
    predictions: Array<TripGooglePlaceAutocompletePrediction>,
  ) => void
  autocompletionReqParam?: AutocompletionRequest
  searchPhrase: string
  isFocused: boolean
  isFullScreen?: boolean
  hasFullScreenMode?: boolean
  onClear?: () => void
  onItemSave: () => void
  onItemDelete: () => void
}

const predictionSelector = (
  prediction: TripGooglePlaceAutocompletePrediction,
  accommodationItems: Array<AccommodationItem>,
) => ({
  title: prediction.structured_formatting.main_text,
  subtitle: (
    <MapSearchPresentationSelector
      secondaryText={prediction.structured_formatting.secondary_text}
      location={{ ...prediction?.location, placeId: prediction.place_id }}
      accommodationItems={accommodationItems}
    />
  ),
})

// Only for Trip planner usage
const MapSearchAutocomplete = React.forwardRef<HTMLInputElement, Props>(
  (
    {
      autocompleteTypes,
      name,
      locationBias,
      onChange,
      onFocus,
      onPlaceSelect,
      accommodationItems,
      onEnterPress,
      autocompletionReqParam,
      helpText,
      endIcon,
      isFocused,
      onFullScreenBlur,
      isFullScreen = false,
      hasFullScreenMode = false,
      onClear,
      searchPhrase,
      onItemSave,
      onItemDelete,
      ...rest
    },
    ref,
  ) => {
    /*
    // Functionality related to the input field itself
    */
    const currentSearchPhrase = useAutoUpdatingRef(searchPhrase)
    const { setValue } = useFormContext()

    const clearSearchPhrase = useCallback(() => {
      setValue(name, undefined)
      onClear?.()
    }, [name, onClear, setValue])

    const handleFocus: FocusEventHandler<HTMLInputElement> = useCallback(
      (e) => {
        onFocus?.(e)
      },
      [onFocus],
    )

    const handleChange: ChangeEventHandler<HTMLInputElement> = useCallback(
      (e) => {
        onChange?.(e)
      },
      [onChange],
    )

    /*
    // Getting the autocomplete predictions
    */
    const autoCompleteRequest: GooglePlaceAutocompleteRequest = useMemo(() => {
      return {
        input: searchPhrase,
        types: autocompleteTypes,
        components: autocompletionReqParam?.componentRestrictions?.country.map(
          (code) => `country:${code}`,
        ),
        locationBias,
      }
      // we don't want to update the request on locationBias change.
      // We only consider the change of locationBias if another thing changes
      // otherwise we'll be making un-necessary requests upon generic map movement.
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
      searchPhrase,
      autocompleteTypes,
      autocompletionReqParam?.componentRestrictions?.country,
    ])

    const {
      hasResults,
      isLoading: predictionsLoading,
      predictions,
    } = useTripPlaceAutocomplete(autoCompleteRequest, currentSearchPhrase, {
      debounceDelay: DEBOUNCE_DELAY,
      characterThreshold: CHARACTER_THRESHOLD,
      includeLocationInPredictions: accommodationItems.length > 0,
    })

    /*
    // On selecting a place functionality
    */
    const [selectedPlaceId, setSelectedPlaceId] = useState<string>('')

    const { data: selectedPlaces } = useTripExplorePlaceDetails({
      placeIds: [selectedPlaceId],
    })

    // This current implementation only supports the selecting of one item
    const currentSelectedItem = selectedPlaces?.[0]

    // This will propagate upwards in the component hierarchy
    const setSelectedItem = useCallback(
      (item: ExploreItem) => {
        onPlaceSelect(item)
      },
      [onPlaceSelect],
    )

    const pendingSetSelectedItem = useRef(false)
    useEffect(() => {
      // If the selected place details are fetched, set the selected item as such
      if (currentSelectedItem && pendingSetSelectedItem.current) {
        setSelectedItem(currentSelectedItem)
        pendingSetSelectedItem.current = false
      }
    }, [currentSelectedItem, setSelectedItem])

    // This is triggered before the useEffect above.
    // The useEffect above is then triggered when the selected place details are fetched.
    const handleSelect = useCallback(
      (option: TripGooglePlaceAutocompletePrediction) => {
        setValue(name, option.structured_formatting.main_text, {
          shouldDirty: true,
          shouldTouch: true,
          shouldValidate: true,
        })
        setSelectedPlaceId(option.place_id)
        // already the selected item, re-select using a deep-copy
        if (currentSelectedItem?.id === option.place_id) {
          setSelectedItem({ ...currentSelectedItem })
        } else {
          pendingSetSelectedItem.current = true
        }
        Analytics.trackClientEvent({
          action: 'click',
          subject: 'map_search_result',
          category: AnalyticsPage.tripPlanner,
          type: 'interaction',
        })
      },
      [setValue, name, currentSelectedItem, setSelectedItem],
    )

    const handleEnterPress = () => {
      const firstPrediction = predictions?.[0]

      if (firstPrediction) {
        handleSelect(firstPrediction)
      }
      onEnterPress?.(predictions)
    }

    const itemStartIcon = (option: TripGooglePlaceAutocompletePrediction) => {
      if (determineItemType(option.types) === ACCOMMODATION_TYPE) {
        return <LineBedIcon />
      }
      return <LineUniversityIcon />
    }

    const itemEndIcon = (option: TripGooglePlaceAutocompletePrediction) => {
      return (
        <SearchBookmarkButtonFromPlaceId
          exploreItemId={option.place_id}
          onSave={onItemSave}
          onDelete={onItemDelete}
        />
      )
    }

    const searchEndIcon = !searchPhrase.length ? (
      endIcon
    ) : (
      <StyledClickable
        aria-label="Clear search"
        title="Clear search"
        onClick={clearSearchPhrase}
      >
        <LineTimesIcon />
      </StyledClickable>
    )

    return (
      <BaseAutocompleteInput
        {...rest}
        canRenderDropdown={
          searchPhrase.length > CHARACTER_THRESHOLD &&
          ((hasFullScreenMode && isFullScreen) ||
            (!hasFullScreenMode && isFocused))
        }
        canRenderNoResult={!hasResults && !predictionsLoading}
        name={name}
        noResultComponent={
          <NoResultsCopy
            message={NO_RESULTS_MESSAGE}
            searchPhrase={searchPhrase}
          />
        }
        onChange={handleChange}
        onFocus={handleFocus}
        onEnterPress={handleEnterPress}
        onOptionSelect={handleSelect}
        options={predictions}
        presentationSelector={(predictions) =>
          predictionSelector(predictions, accommodationItems)
        }
        ref={ref}
        isLoading={predictionsLoading}
        helpText={helpText}
        endIcon={searchEndIcon}
        isFullScreen={isFullScreen}
        itemStartIcon={itemStartIcon}
        itemEndIcon={itemEndIcon}
        hasGutter={isFullScreen}
        noValidationSpacing
      />
    )
  },
)

MapSearchAutocomplete.displayName = 'MapSearchAutocomplete'
export default MapSearchAutocomplete
